import { useState, useEffect, useRef, useMemo } from "react";
import { Button } from "../../components/ui/button";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faEye,
  faKey,
  faUserShield,
  faLock,
  faPenToSquare,
  faChevronDown,
  faUsers,
  faUserCheck,
  faUserSlash,
  faSpinner,
} from "@fortawesome/free-solid-svg-icons";
import { Card } from "../../components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui/table";
import { Badge } from "../../components/ui/badge";
import { Link } from "react-router-dom";
import AddCustomerModal from "./customer/AddCustomerModal";
import ViewDetailsModal from "./customer/ViewDetailsModal";
import EditDetailsModal from "./customer/EditDetailsModal";
import ManagePositionModal from "./customer/ManagePositionModal";
import ChangePasswordModal from "./customer/ChangePasswordModal";
import SearchBar from "../../components/ui/SearchBar";
import DateFilter from "../../components/ui/DateFilter";
import EmptyState from "../../components/ui/EmptyState";
import Pagination from "../../components/ui/Pagination";
import { userService } from "../../services/service";
import { toastError } from "../../utils/toast";

const statusStyles = {
  ACTIVE: "bg-green-50 text-green-700 border-green-200",
  INACTIVE: "bg-red-50 text-red-700 border-red-200",
  active: "bg-green-50 text-green-700 border-green-200",
  inactive: "bg-red-50 text-red-700 border-red-200",
};

const statusDotStyles = {
  ACTIVE: "bg-green-500",
  INACTIVE: "bg-red-500",
  active: "bg-green-500",
  inactive: "bg-red-500",
};

const positionStyles = {
  seller: "bg-blue-50 text-blue-700 border-blue-200",
  owner: "bg-purple-50 text-purple-700 border-purple-200",
  Seller: "bg-blue-50 text-blue-700 border-blue-200",
  "Shop Owner": "bg-purple-50 text-purple-700 border-purple-200",
};

const ManageCustomer = () => {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeModal, setActiveModal] = useState(null);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [openMenuId, setOpenMenuId] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [activeTab, setActiveTab] = useState("all");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(5);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [isFilterApplied, setIsFilterApplied] = useState(false);

  const containerRef = useRef(null);
  useEffect(() => {
    if (!openMenuId) return;
    const handleOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpenMenuId(null);
      }
    };
    document.addEventListener("mousedown", handleOutside);
    return () => document.removeEventListener("mousedown", handleOutside);
  }, [openMenuId]);

  const fetchCustomers = async () => {
    try {
      setLoading(true);
      const response = await userService.getAllUsersWithRoles();
      let userData = [];

      if (response?.data) {
        if (Array.isArray(response.data)) {
          userData = response.data;
        } else if (response.data.data && Array.isArray(response.data.data)) {
          userData = response.data.data;
        } else if (Array.isArray(response)) {
          userData = response;
        } else if (response.users && Array.isArray(response.users)) {
          userData = response.users;
        }
      }

      const filteredData = userData.filter(
        (user) => user.role === "seller" || user.role === "owner",
      );

      const transformedData = filteredData.map((user, index) => ({
        userId: user.userId || user.id,
        customer_no: user.username || `CU${String(index + 1).padStart(3, "0")}`,
        full_name: user.full_name || "N/A",
        phone_number: user.phone || "N/A",
        email: user.email || "N/A",
        position: user.role === "seller" ? "Seller" : "Shop Owner",
        status: user.status?.toUpperCase() || "ACTIVE",
        address: "N/A",
        created_at: user.created_at || new Date().toISOString(),
        updated_at: user.updated_at || new Date().toISOString(),
        role: user.role,
      }));

      setCustomers(transformedData);
    } catch (error) {
      console.error("Failed to fetch customers:", error);
      toastError("Failed to load customers. Please try again.");
      setCustomers([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCustomers();
  }, []);

  const openModal = (type, customer = null) => {
    setSelectedCustomer(customer);
    setActiveModal(type);
  };

  const closeModal = () => {
    setActiveModal(null);
    setSelectedCustomer(null);
  };

  const handleAddCustomer = (newCustomer) => {
    fetchCustomers();
  };

  const handleSaveEdit = (updatedCustomer) => {
    setCustomers((prev) =>
      prev.map((c) =>
        c.userId === updatedCustomer.userId ? updatedCustomer : c,
      ),
    );
  };

  const handleSaveRole = (updatedCustomer) => {
    setCustomers((prev) =>
      prev.map((c) =>
        c.userId === updatedCustomer.userId ? updatedCustomer : c,
      ),
    );
  };

  const filteredCustomers = useMemo(() => {
    let result = customers;

    if (activeTab === "active") {
      result = result.filter((c) => c.status === "ACTIVE");
    } else if (activeTab === "inactive") {
      result = result.filter((c) => c.status === "INACTIVE");
    }

    if (isFilterApplied && startDate && endDate) {
      const start = new Date(startDate);
      const end = new Date(endDate);
      end.setHours(23, 59, 59, 999);
      result = result.filter((c) => {
        const createdDate = new Date(c.created_at);
        return createdDate >= start && createdDate <= end;
      });
    }

    if (searchTerm.trim()) {
      const search = searchTerm.toLowerCase().trim();
      result = result.filter(
        (c) =>
          c.full_name.toLowerCase().includes(search) ||
          c.customer_no.toLowerCase().includes(search) ||
          c.phone_number.includes(search) ||
          c.email.toLowerCase().includes(search) ||
          c.position.toLowerCase().includes(search),
      );
    }

    return result;
  }, [customers, activeTab, searchTerm, startDate, endDate, isFilterApplied]);

  const totalItems = filteredCustomers.length;
  const totalPages = Math.ceil(totalItems / pageSize);
  const startIndex = (currentPage - 1) * pageSize;
  const currentCustomers = filteredCustomers.slice(
    startIndex,
    startIndex + pageSize,
  );

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, activeTab, startDate, endDate, isFilterApplied]);

  const tabCounts = {
    all: customers.length,
    active: customers.filter((c) => c.status === "ACTIVE").length,
    inactive: customers.filter((c) => c.status === "INACTIVE").length,
  };

  const tabs = [
    { id: "all", label: "All customers", icon: faUsers },
    { id: "active", label: "Active", icon: faUserCheck },
    { id: "inactive", label: "Inactive", icon: faUserSlash },
  ];

  const getEmptyStateMessage = () => {
    if (searchTerm || isFilterApplied) {
      return "Try adjusting your search or filters";
    }
    if (activeTab !== "all") {
      return `No ${activeTab} customers found`;
    }
    return "Add your first customer to get started";
  };

  if (loading) {
    return (
      <div className="flex min-h-[400px] items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <FontAwesomeIcon
            icon={faSpinner}
            className="h-12 w-12 text-blue-600 animate-spin"
          />
          <p className="text-gray-600 text-sm">Loading customers...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4 sm:gap-6" ref={containerRef}>
      <AddCustomerModal
        isOpen={activeModal === "add"}
        onClose={closeModal}
        onAdd={handleAddCustomer}
        onUserAdded={fetchCustomers}
      />
      <ViewDetailsModal
        customer={selectedCustomer}
        isOpen={activeModal === "view"}
        onClose={closeModal}
      />
      <EditDetailsModal
        customer={selectedCustomer}
        isOpen={activeModal === "edit"}
        onClose={closeModal}
        onSave={handleSaveEdit}
        onUserUpdated={fetchCustomers}
      />
      <ManagePositionModal
        customer={selectedCustomer}
        isOpen={activeModal === "role"}
        onClose={closeModal}
        onSave={handleSaveRole}
        onUserUpdated={fetchCustomers}
      />
      <ChangePasswordModal
        customer={selectedCustomer}
        isOpen={activeModal === "password"}
        onClose={closeModal}
        onUserUpdated={fetchCustomers}
      />

      <Card className="bg-white border border-gray-200 px-4 sm:px-6 py-4 shadow-sm">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h2 className="text-xl sm:text-2xl font-semibold text-gray-900">
              Manage Customers
            </h2>
            <p className="text-xs sm:text-sm text-gray-600 mt-0.5">
              Manage all customer details including positions, permissions,
              passwords and status
            </p>
          </div>
          {/* <Button
            className="gap-2 w-full sm:w-auto bg-blue-800 hover:bg-blue-900 text-white shadow-sm"
            onClick={() => openModal("add")}
          >
            <FontAwesomeIcon icon={faPlus} />
            Add customer
          </Button> */}
        </div>
      </Card>

      <DateFilter
        startDate={startDate}
        endDate={endDate}
        onStartDateChange={setStartDate}
        onEndDateChange={setEndDate}
        onApply={() => {
          setIsFilterApplied(true);
          setCurrentPage(1);
        }}
        onClear={() => {
          setStartDate("");
          setEndDate("");
          setIsFilterApplied(false);
          setCurrentPage(1);
        }}
        isFilterApplied={isFilterApplied}
        totalItems={filteredCustomers.length}
        label="customers"
      />

      <Card className="bg-white border border-gray-200 px-4 sm:px-6 py-4 shadow-sm">
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
          <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-1 w-full lg:w-auto">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-3 sm:px-4 py-2 rounded-md text-sm font-medium transition-all duration-200 flex-1 lg:flex-none justify-center ${
                  activeTab === tab.id
                    ? "bg-blue-800 text-white shadow-sm"
                    : "text-gray-600 hover:text-gray-900 hover:bg-gray-200/50"
                }`}
              >
                <FontAwesomeIcon icon={tab.icon} className="h-3.5 w-3.5" />
                <span className="hidden sm:inline">{tab.label}</span>
                <span
                  className={`ml-0 sm:ml-1 px-2 py-0.5 text-xs rounded-full ${
                    activeTab === tab.id
                      ? "bg-blue-100 text-blue-800"
                      : "bg-gray-200 text-gray-600"
                  }`}
                >
                  {tabCounts[tab.id]}
                </span>
              </button>
            ))}
          </div>

          <SearchBar
            value={searchTerm}
            onChange={(value) => {
              setSearchTerm(value);
              setCurrentPage(1);
            }}
            placeholder="Search by name, customer no, phone, email or position..."
            className="w-full lg:w-80"
          />
        </div>
      </Card>

      <Card className="bg-white border border-gray-200 shadow-sm overflow-hidden">
        <div className="px-4 sm:px-6 pt-4 pb-2 border-b border-gray-100">
          <div className="flex items-center justify-between">
            <div className="text-sm text-gray-600">
              Showing{" "}
              <span className="font-semibold text-gray-700">{totalItems}</span>{" "}
              customers
              {activeTab !== "all" && (
                <span className="ml-1">({activeTab})</span>
              )}
              {isFilterApplied && (
                <span className="ml-1 text-blue-600">(filtered)</span>
              )}
            </div>
            <div className="text-sm text-gray-600">
              {currentCustomers.length > 0 &&
                `Page ${currentPage} of ${totalPages}`}
            </div>
          </div>
        </div>

        <div className="overflow-x-auto">
          <Table className="min-w-[800px]">
            <TableHeader>
              <TableRow className="bg-gray-50 hover:bg-gray-50 border-b-2 border-gray-200">
                <TableHead className="text-xs font-semibold text-gray-600 uppercase tracking-wider py-3 px-4">
                  S/N
                </TableHead>
                <TableHead className="text-xs font-semibold text-gray-600 uppercase tracking-wider py-3 px-4">
                  Customer No
                </TableHead>
                <TableHead className="text-xs font-semibold text-gray-600 uppercase tracking-wider py-3 px-4">
                  Full Name
                </TableHead>
                <TableHead className="text-xs font-semibold text-gray-600 uppercase tracking-wider py-3 px-4">
                  Phone
                </TableHead>
                <TableHead className="text-xs font-semibold text-gray-600 uppercase tracking-wider py-3 px-4">
                  Position
                </TableHead>
                <TableHead className="text-xs font-semibold text-gray-600 uppercase tracking-wider py-3 px-4">
                  Status
                </TableHead>
                <TableHead className="text-xs font-semibold text-gray-600 uppercase tracking-wider py-3 px-4 text-center">
                  Actions
                </TableHead>
              </TableRow>
            </TableHeader>

            <TableBody>
              {currentCustomers.length > 0 ? (
                currentCustomers.map((customer, index) => (
                  <TableRow
                    key={customer.userId}
                    className="hover:bg-blue-50/50 transition-colors duration-150 border-b border-gray-100"
                  >
                    <TableCell className="py-3 px-4 text-sm text-gray-600">
                      {startIndex + index + 1}
                    </TableCell>
                    <TableCell className="py-3 px-4 font-medium text-gray-900 text-sm">
                      {customer.customer_no}
                    </TableCell>
                    <TableCell className="py-3 px-4 text-sm text-gray-700">
                      {customer.full_name}
                    </TableCell>
                    <TableCell className="py-3 px-4 text-sm text-gray-600">
                      {customer.phone_number}
                    </TableCell>
                    <TableCell className="py-3 px-4">
                      <Badge
                        variant="outline"
                        className={`${positionStyles[customer.position] || positionStyles.seller} border font-normal px-3 py-1 rounded-full text-xs`}
                      >
                        {customer.position}
                      </Badge>
                    </TableCell>
                    <TableCell className="py-3 px-4">
                      <Badge
                        variant="outline"
                        className={`${statusStyles[customer.status]} border font-normal px-3 py-1 rounded-full text-xs flex items-center gap-1.5`}
                      >
                        <span
                          className={`inline-block w-1.5 h-1.5 rounded-full ${statusDotStyles[customer.status]}`}
                        />
                        {customer.status}
                      </Badge>
                    </TableCell>
                    <TableCell className="py-3 px-4">
                      <div className="relative inline-block w-full">
                        <Button
                          size="sm"
                          variant="outline"
                          className="gap-2 w-full sm:w-auto border-gray-300 hover:bg-gray-50 hover:border-blue-800 text-gray-700"
                          onClick={() =>
                            setOpenMenuId(
                              openMenuId === customer.userId
                                ? null
                                : customer.userId,
                            )
                          }
                        >
                          <span>Actions</span>
                          <FontAwesomeIcon
                            icon={faChevronDown}
                            className={`h-3 w-3 transition-transform duration-200 ${
                              openMenuId === customer.userId ? "rotate-180" : ""
                            }`}
                          />
                        </Button>

                        {openMenuId === customer.userId && (
                          <div className="absolute right-0 mt-1 w-52 rounded-lg border border-gray-200 bg-white shadow-lg z-50 overflow-hidden animate-in fade-in duration-150">
                            <button
                              onClick={() => {
                                openModal("view", customer);
                                setOpenMenuId(null);
                              }}
                              className="w-full flex items-center gap-3 px-4 py-2.5 text-sm hover:bg-gray-50 transition-colors"
                            >
                              <FontAwesomeIcon
                                icon={faEye}
                                className="text-gray-600 h-3.5 w-3.5"
                              />
                              View Details
                            </button>

                            <button
                              onClick={() => {
                                openModal("edit", customer);
                                setOpenMenuId(null);
                              }}
                              className="w-full flex items-center gap-3 px-4 py-2.5 text-sm hover:bg-gray-50 transition-colors"
                            >
                              <FontAwesomeIcon
                                icon={faPenToSquare}
                                className="text-gray-600 h-3.5 w-3.5"
                              />
                              Edit Details
                            </button>

                            <button
                              onClick={() => {
                                openModal("role", customer);
                                setOpenMenuId(null);
                              }}
                              className="w-full flex items-center gap-3 px-4 py-2.5 text-sm hover:bg-gray-50 transition-colors"
                            >
                              <FontAwesomeIcon
                                icon={faUserShield}
                                className="text-gray-600 h-3.5 w-3.5"
                              />
                              Manage Position
                            </button>

                            <Link
                              to={`/app/customers/${customer.userId}/permissions`}
                              className="w-full flex items-center gap-3 px-4 py-2.5 text-sm hover:bg-gray-50 transition-colors"
                              onClick={() => setOpenMenuId(null)}
                            >
                              <FontAwesomeIcon
                                icon={faKey}
                                className="text-gray-600 h-3.5 w-3.5"
                              />
                              Manage Permissions
                            </Link>

                            <div className="border-t border-gray-100">
                              <button
                                onClick={() => {
                                  openModal("password", customer);
                                  setOpenMenuId(null);
                                }}
                                className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-orange-600 hover:bg-orange-50 transition-colors"
                              >
                                <FontAwesomeIcon
                                  icon={faLock}
                                  className="h-3.5 w-3.5"
                                />
                                Change Password
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-8">
                    <EmptyState
                      title="No customers found"
                      message={getEmptyStateMessage()}
                    />
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>

        {totalItems > 0 && totalPages > 0 && (
          <div className="px-4 sm:px-6 py-3 border-t border-gray-100">
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              pageSize={pageSize}
              totalItems={totalItems}
              onPageChange={setCurrentPage}
              onPageSizeChange={setPageSize}
            />
          </div>
        )}
      </Card>
    </div>
  );
};

export default ManageCustomer;
